import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { withLatestFrom, switchMap, catchError, of, map } from "rxjs";
import { ContractClient } from "src/api-service";
import { selectSelectedCompany } from "../../../user/user.selectors";
import { selectContractState } from "../contract.selectors";
import { ContractsActions } from "./contracts.actions";
import { normalizeError } from "../../../app-store-error.types";

// ============================================================================
// CONTRACTS LIST EFFECTS - Single effect, no chained actions
// ============================================================================

@Injectable()
export class ContractsEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly contractClient = inject(ContractClient);

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractsActions.load),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectContractState),
            ),
            switchMap(([action, selectedCompany, state]) => {
                const companyId = selectedCompany?.id;
                if (!companyId) {
                    return of(ContractsActions.loadFailure({
                        error: { message: "No company selected" },
                        toastKey: "CONTRACT_LIST.TOAST.LOAD_ERROR",
                    }));
                }

                // Merge: action params → current state → defaults
                const params = {
                    searchFilter: action.searchFilter ?? state?.searchFilter ?? "",
                    startDateFrom: action.startDateFrom ?? state?.startDateFrom ?? null,
                    startDateTo: action.startDateTo ?? state?.startDateTo ?? null,
                    endDateFrom: action.endDateFrom ?? state?.endDateFrom ?? null,
                    endDateTo: action.endDateTo ?? state?.endDateTo ?? null,
                };

                return this.contractClient
                    .getAll(companyId)
                    .pipe(
                        map(contracts => ContractsActions.loadSuccess({
                            contracts,
                            params,
                        })),
                        catchError(error => of(ContractsActions.loadFailure({
                            error: normalizeError(error),
                            toastKey: "CONTRACT_LIST.TOAST.LOAD_ERROR",
                        }))),
                    );
            }),
        ),
    );
}
